<script setup lang="ts">
import { usePage } from '@inertiajs/vue3';
import PublicLayout from '@/layouts/PublicLayout.vue';
import type { LegalData, SiteSettings } from '@/types';

type Props = {
    legalData: LegalData;
};

defineProps<Props>();

const page = usePage();
const settings = page.props.siteSettings as SiteSettings;
</script>

<template>
    <PublicLayout>
        <div class="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
            <h1 class="text-3xl font-bold tracking-tight sm:text-4xl">
                Conditions générales de vente
            </h1>

            <div
                class="prose mt-8 max-w-none prose-invert prose-headings:font-display prose-headings:text-peniche-cream prose-a:text-brand prose-a:hover:text-brand-hover prose-strong:text-peniche-cream"
            >
                <h2>Éditeur</h2>
                <p>
                    <strong>{{
                        legalData.company_name ||
                        settings.business_name ||
                        '[Non renseigné]'
                    }}</strong>
                    <template v-if="legalData.company_form">
                        ({{ legalData.company_form }})
                    </template>
                </p>

                <p
                    v-if="
                        settings.address || settings.city || settings.zip_code
                    "
                >
                    <strong>Siège social :</strong>
                    {{ settings.address }}, {{ settings.zip_code }}
                    {{ settings.city }}
                </p>

                <p v-if="legalData.rcs">
                    <strong>Immatriculation :</strong> {{ legalData.rcs }}
                </p>

                <p v-if="legalData.vat_number">
                    <strong>Numéro de TVA intracommunautaire :</strong>
                    {{ legalData.vat_number }}
                </p>

                <p v-if="settings.phone">
                    <strong>Téléphone :</strong>
                    <a :href="`tel:${settings.phone.replace(/\s/g, '')}`">{{
                        settings.phone
                    }}</a>
                </p>

                <p v-if="settings.email">
                    <strong>Email :</strong>
                    <a :href="`mailto:${settings.email}`">{{
                        settings.email
                    }}</a>
                </p>

                <p>
                    <strong>Directeur de la publication :</strong>
                    {{
                        legalData.publication_director ||
                        legalData.director ||
                        '[Non renseigné]'
                    }}
                </p>

                <p v-if="legalData.hosting_company">
                    <strong>Hébergement :</strong>
                    {{ legalData.hosting_company
                    }}<template v-if="legalData.hosting_address"
                        >, {{ legalData.hosting_address }}</template
                    >
                </p>

                <h2>Conditions de réservation</h2>

                <h3>1. Objet</h3>
                <p>
                    Les présentes conditions définissent les modalités de
                    réservation proposées par l'Auberge La Péniche.
                </p>

                <h3>2. Réservation</h3>
                <p>
                    Les demandes de réservation peuvent être effectuées par
                    téléphone, email ou via le formulaire de contact du site.
                </p>
                <p>
                    La réservation est considérée comme confirmée après
                    validation par l'établissement.
                </p>

                <h3>3. Prix</h3>
                <p>Les prix sont indiqués en euros TTC.</p>
                <p>
                    La taxe de séjour est à régler sur place selon la
                    réglementation en vigueur.
                </p>

                <h3>4. Paiement</h3>
                <p>
                    Le règlement s'effectue directement sur place lors du
                    séjour, sauf mention contraire.
                </p>

                <h3>5. Responsabilité</h3>
                <p>
                    L'établissement ne saurait être tenu responsable en cas de
                    mauvaise utilisation du site ou d'indisponibilité
                    temporaire.
                </p>

                <h2>Conditions d'annulation</h2>
                <p>
                    Toute annulation doit être communiquée par écrit (email ou
                    courrier).
                </p>
                <ul>
                    <li>
                        Annulation gratuite jusqu'à 48 heures avant la date
                        d'arrivée
                    </li>
                    <li>
                        En cas d'annulation tardive : facturation de la première
                        nuit
                    </li>
                    <li>
                        En cas de non-présentation (no-show) : facturation de la
                        première nuit
                    </li>
                    <li>
                        Pour certaines périodes ou offres spécifiques, des
                        conditions particulières peuvent s'appliquer
                    </li>
                </ul>
                <p>
                    L'établissement se réserve le droit de demander un acompte
                    pour garantir certaines réservations.
                </p>

                <h2>Contact</h2>
                <p v-if="settings.email">
                    Pour toute question relative aux présentes conditions, vous
                    pouvez nous contacter à :
                    <a :href="`mailto:${settings.email}`">{{
                        settings.email
                    }}</a>
                </p>
            </div>
        </div>
    </PublicLayout>
</template>
